import React, { useEffect, useState } from 'react'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Typography, Box } from '@mui/material'
import { ApiView } from '@e3dc-react/shell/Elements/ApiView'
import { makeStyles } from 'tss-react/mui'
import { useDispatch } from 'react-redux'
import { isUndefined } from 'lodash'
import { useParams, useNavigate } from 'react-router-dom'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'

import { useCompanyDataTabs } from '../hooks/use-company-tabs'
import { CompanyModel } from '../models/company.model'
import { useCompanies } from '../hooks/use-companies'
import { usePageActionsContext } from 'src/Libs/header/context'
import { ApiTableColumn } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { UseFetchProps } from '@e3dc-react/shell/Hooks/useFetch'

const useStyles = makeStyles()(theme => ({
  table: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
  tableHeader: {
    whiteSpace: 'pre-line',
  },
  bodyClassName: {
    '&:hover > td': {
      opacity: 1,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark,
    },
  },
}))

interface CompanyApiTableTabProps<T> {
  columns: ApiTableColumn<T>[]
  fetchProps: UseFetchProps
  title: string
  tabKey: number
  customPayload?: Record<string, string>
  customNoDataTitle?: string
}

/**
 * Common component to show data that relates to a company(e.g. history, installations)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CompanyApiTableTab = <T extends { [key: string]: any }>({
  columns,
  fetchProps,
  title,
  tabKey,
  customPayload,
  customNoDataTitle,
}: CompanyApiTableTabProps<T>): JSX.Element => {
  const { dispatchPageActions } = usePageActionsContext()
  const { setActiveTab } = useCompanyDataTabs()
  const [company, setCompany] = useState<CompanyModel>()
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const { getCompany } = useCompanies()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const companyId = Number(id)

  useEffect(() => {
    setActiveTab(tabKey)
  }, [setActiveTab, tabKey])

  useEffect(() => {
    dispatchPageActions?.([{ position: 'left', type: 'back', onClick: () => navigate('/overview/companies') }])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, navigate])

  useEffect(() => {
    dispatch(appUpdate({ title: company?.name }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]) // eslint-disable-line

  useEffect(() => {
    if (isUndefined(companyId)) return
    getCompany(companyId)
      .then(company => {
        setCompany(company)
      })
      .catch(error => console.error(error))
  }, [companyId]) // eslint-disable-line

  return (
    <Paper
      title={
        <Typography variant={'h3'} component={'h3'}>
          {title}
        </Typography>
      }
    >
      <Box className={classes.table}>
        <ApiView<T>
          fetchProps={fetchProps}
          tableColumns={columns}
          isMobilePaginator={true}
          onlyTable
          hasColoredTableColumns
          bodyElevation={0}
          footerElevation={0}
          headerElevation={0}
          noData={customNoDataTitle}
          headerClassName={classes.tableHeader}
          bodyClassName={classes.bodyClassName}
          customPayload={customPayload}
        />
      </Box>
    </Paper>
  )
}

export default CompanyApiTableTab
